import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, authHeaders } from './apiHelpers';
import { MARKET } from '../constants/storageKeys';

export interface InfoMessage {
    infoMessageId: number;
    message: string;
    showFromDateUTC: Date;
    showToDateUTC?: Date;
    updateDateUTC: Date;
    title: string;
    cultureId: string;
    lastUpdatedBy: string;
}

export function useInfoMessages(cultureCode: string, { enabled = true }: { enabled?: boolean } = {}) {
    const market = window.localStorage.getItem(MARKET) ?? 'se';
    return useQuery<InfoMessage[], Error>({
        queryKey: ['infoMessages', cultureCode, market],
        queryFn: () => apiFetch<InfoMessage[]>(`api/InfoMessages/GetMessagesForDrivers?cultureCode=${encodeURIComponent(cultureCode)}&market=${encodeURIComponent(market)}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: enabled && !!cultureCode
    });
}

export function useAdminInfoMessages({ enabled = true }: { enabled?: boolean } = {}) {
    const market = window.localStorage.getItem(MARKET) ?? 'se';
    return useQuery<InfoMessage[], Error>({
        queryKey: ['adminInfoMessages', market],
        queryFn: () => apiFetch<InfoMessage[]>(`api/InfoMessages/GetMessagesForAdmins?market=${encodeURIComponent(market)}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled
    });
}

export function useInfoMessage(infoMessageId: number) {
    return useQuery<InfoMessage, Error>({
        queryKey: ['infoMessage', infoMessageId],
        queryFn: () => apiFetch<InfoMessage>(`api/InfoMessages/GetMessage?infoMessageId=${infoMessageId}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: infoMessageId > 0
    });
}

export function useAddInfoMessage() {
    const queryClient = useQueryClient();
    return useMutation<number, Error, { message: InfoMessage; market: string }>({
        mutationFn: ({ message, market }) => apiFetch<number>(`api/InfoMessages/Add?market=${encodeURIComponent(market)}`, {
            method: 'POST',
            body: JSON.stringify(message),
            headers: authHeaders()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adminInfoMessages'] })
    });
}

export function useEditInfoMessage() {
    const queryClient = useQueryClient();
    return useMutation<InfoMessage, Error, InfoMessage>({
        mutationFn: (message) => apiFetch<InfoMessage>('api/InfoMessages/Update', {
            method: 'POST',
            body: JSON.stringify(message),
            headers: authHeaders()
        }),
        onSuccess: (msg) => {
            queryClient.invalidateQueries({ queryKey: ['adminInfoMessages'] });
            queryClient.invalidateQueries({ queryKey: ['infoMessage', msg.infoMessageId] });
        }
    });
}

export function useDeleteInfoMessage() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, number>({
        mutationFn: (id) => apiFetch<void>(`api/InfoMessages/Delete?id=${id}`, {
            method: 'DELETE',
            headers: authHeaders()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adminInfoMessages'] })
    });
}
